import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, X } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { waLink } from "@/config";

const NAV = [
  { href: "#inicio", label: "Início" },
  { href: "#categorias", label: "Categorias" },
  { href: "#catalogo", label: "Catálogo" },
  { href: "#servicos", label: "Serviços" },
  { href: "#sobre", label: "Sobre" },
  { href: "#contato", label: "Contato" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-parchment/85 shadow-[0_1px_0_0_rgba(226,220,206,0.8)] backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-12 lg:px-20">
        <a href="#inicio" data-testid="header-logo-link" aria-label="Agro Carmo — voltar ao início">
          <LogoMark />
        </a>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navegação principal">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
              className="group relative text-sm font-medium text-ink/80 transition-colors duration-300 hover:text-olive"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-clay transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink("Olá, Agro Carmo! Vim pelo site e quero fazer um pedido.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="hidden items-center gap-2 rounded-full bg-olive px-5 py-2.5 text-sm font-semibold text-parchment transition-colors duration-300 hover:bg-forest sm:inline-flex"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex rounded-full border border-linen bg-parchment/70 p-2.5 text-olive backdrop-blur lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden border-t border-linen bg-parchment/95 backdrop-blur-xl lg:hidden"
            aria-label="Menu móvel"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-3 font-display text-lg text-ink transition-colors hover:bg-linen/50"
                >
                  {item.label}
                </a>
              ))}
              <a
                href={waLink("Olá, Agro Carmo! Vim pelo site e quero fazer um pedido.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-button"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-olive px-5 py-3 text-sm font-semibold text-parchment"
              >
                <MessageCircle className="h-4 w-4" />
                Pedir no WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
